import { CHANNEL_NAMES, PRIVACY_DEFAULTS } from '@wms/domain';
import { JOB_TOPIC_LABELS, type OpsMarketplace, type OpsProblem, type OpsStatus } from '@wms/domain';
import { useState } from 'react';
import { api, errorMessage } from '../../api';
import { fmt } from '../../format';
import { Empty, ErrorBanner, SellerChip, Spinner, useLoad, useToast } from '../../ui';
import { Icon } from '../../ui/icons';

const CHANNELS: Readonly<Record<string, string>> = CHANNEL_NAMES;
const CREDENTIAL_STATUS: Readonly<Record<string, { label: string; tone: string }>> = {
  active: { label: 'Aktif', tone: 'ok' },
  auth_failed: { label: 'Yetki hatası', tone: 'bad' },
  disabled: { label: 'Kapalı', tone: '' },
};

/** When orders were last pulled (an account from before the pull time was kept: where its window ended). */
function lastPull(m: OpsMarketplace): string {
  const at = m.pulledAt ?? m.syncedUntil;
  return at ? `${fmt.age(at)} önce` : `hiç (${fmt.age(m.connectedAt)} önce bağlandı)`;
}

/**
 * Sistem durumu: background jobs that failed, marketplace syncs that went quiet, the worker's
 * heartbeat and the queue. Supervisors retry or dismiss from here instead of finding out from
 * a seller that orders stopped coming.
 */
export function Operations({ onChange }: { onChange: () => void }) {
  const status = useLoad(() => api<OpsStatus>('GET', '/api/admin/operations'), [], 15_000);
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);

  const act = async (key: string, path: string, done: string) => {
    setBusy(key);
    try {
      await api('POST', path);
      toast(done, 'ok');
      await status.reload();
      onChange();
    } catch (e) {
      toast(errorMessage(e), 'bad');
    } finally {
      setBusy(null);
    }
  };

  if (status.error) return <ErrorBanner message={status.error} onRetry={() => void status.reload()} />;
  if (!status.data) return <Spinner />;
  const s = status.data;
  const critical = s.problems.filter((p) => p.severity === 'critical').length;
  const warning = s.problems.length - critical;
  const brokenAccounts = s.marketplaces.filter((m) => m.status === 'auth_failed' || m.stale).length;

  const sync = (m: Pick<OpsMarketplace, 'id'>) => act(`sync:${m.id}`, `/api/admin/operations/marketplaces/${m.id}/sync`, 'Senkron kuyruğa alındı; sonuç birkaç dakika içinde burada görünür');

  return (
    <>
      <div className="row">
        <span className="muted">
          Son kontrol {fmt.dateTime(s.checkedAt)} · her 15 saniyede yenilenir
        </span>
        <span className="spacer" />
        <button className="btn sm" onClick={() => void status.reload()}>
          <Icon name="refresh" size={16} /> Yenile
        </button>
      </div>

      <div className="grid">
        <div className={`card kpi${s.worker && !s.worker.alive ? ' alert' : ''}`}>
          <span className="n" style={{ fontSize: 22 }}>
            {!s.worker ? 'Sinyal yok' : s.worker.alive ? 'Çalışıyor' : 'Durdu'}
          </span>
          <span className="l">Arka plan servisi</span>
          <span className="h">{s.worker ? `Son sinyal ${fmt.age(s.worker.seenAt)} önce · ${s.worker.name}` : 'Servis henüz hiç sinyal göndermedi'}</span>
        </div>
        <div className={`card kpi${s.queue.delayed > 0 ? ' alert' : ''}`}>
          <span className="n">{fmt.n(s.queue.pending)}</span>
          <span className="l">Kuyrukta bekleyen iş</span>
          <span className="h">{s.queue.delayed > 0 ? `${fmt.n(s.queue.delayed)} iş 5 dakikadan uzun süredir bekliyor` : 'Gecikme yok'}</span>
        </div>
        <div className={`card kpi${brokenAccounts > 0 ? ' alert' : ''}`}>
          <span className="n">
            {fmt.n(s.marketplaces.length - brokenAccounts)}/{fmt.n(s.marketplaces.length)}
          </span>
          <span className="l">Sağlıklı pazaryeri hesabı</span>
          <span className="h">{brokenAccounts > 0 ? `${brokenAccounts} hesapta senkron sorunu` : 'Siparişler düzenli çekiliyor'}</span>
        </div>
        <div className={`card kpi${critical > 0 ? ' alert' : ''}`}>
          <span className="n">{fmt.n(s.problems.length)}</span>
          <span className="l">Açık sorun</span>
          <span className="h">{s.problems.length === 0 ? 'Her şey yolunda' : `${critical} kritik, ${warning} uyarı`}</span>
        </div>
      </div>

      <section className="card">
        <div className="card-h">
          <Icon name="alert" />
          <h2>Sorunlar</h2>
          <span className="sub">Kritik olanlar önce; çözülen iş kendiliğinden listeden düşer</span>
        </div>
        {s.problems.length === 0 ? (
          <Empty title="Açık sorun yok">Arka plan işleri, pazaryeri senkronları ve yazdırma kuyruğu sorunsuz.</Empty>
        ) : (
          <div className="ops-list">
            {s.problems.map((p) => (
              <ProblemRow
                key={p.key}
                problem={p}
                busy={busy}
                onRetry={(id) => void act(`retry:${id}`, `/api/admin/operations/failures/${id}/retry`, 'İş yeniden gönderildi')}
                onDismiss={(id) => void act(`dismiss:${id}`, `/api/admin/operations/failures/${id}/dismiss`, 'Kayıt kapatıldı')}
                onSync={(id) => void sync({ id })}
              />
            ))}
          </div>
        )}
      </section>

      <section className="card">
        <div className="card-h">
          <Icon name="plug" />
          <h2>Pazaryeri bağlantıları</h2>
          <span className="sub">Siparişler 2 dakikada bir çekilir; 15 dakikayı geçen sessizlik sorun sayılır</span>
        </div>
        {s.marketplaces.length === 0 ? (
          <Empty title="Bağlı pazaryeri hesabı yok" />
        ) : (
          <div className="table-wrap">
            <table className="t stack-sm">
              <thead>
                <tr>
                  <th>Satıcı</th>
                  <th>Kanal</th>
                  <th>Durum</th>
                  <th>Son senkron</th>
                  <th>Son hata</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {s.marketplaces.map((m) => {
                  const st = CREDENTIAL_STATUS[m.status] ?? { label: m.status, tone: '' };
                  return (
                    <tr key={m.id}>
                      <td>
                        <SellerChip code={m.tenantCode} />
                      </td>
                      <td>
                        <b>{CHANNELS[m.channel] ?? m.channel}</b>
                        <div className="muted" style={{ fontSize: 12 }}>
                          {m.label}
                        </div>
                      </td>
                      <td>
                        <span className={`chip ${m.stale ? 'bad' : st.tone}`}>{m.stale ? 'Gecikiyor' : st.label}</span>
                        {!m.takingOrders && (
                          <div className="muted" style={{ fontSize: 12 }}>
                            Sipariş alımı başlamadı
                          </div>
                        )}
                      </td>
                      <td data-label="Son senkron">{lastPull(m)}</td>
                      <td className="muted" style={{ fontSize: 12, maxWidth: 320 }}>
                        {m.lastError ?? '—'}
                      </td>
                      <td className="num">
                        {m.status === 'active' && (
                          <button className="btn sm" disabled={busy !== null} onClick={() => void sync(m)}>
                            <Icon name="refresh" size={16} /> Şimdi çek
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {s.keyring && (
        <section className="card">
          <div className="card-h">
            <Icon name="key" />
            <h2>Şifreleme anahtarları</h2>
            <span className="sub">Pazaryeri, kargo ve iki adımlı doğrulama sırlarını açan anahtarlar; burada yalnız parmak izleri görünür</span>
          </div>
          <div className="table-wrap">
            <table className="t">
              <thead>
                <tr>
                  <th>Anahtar</th>
                  <th>Parmak izi</th>
                  <th className="num">Şifrelediği sır</th>
                </tr>
              </thead>
              <tbody>
                {s.keyring.keys.map((k) => (
                  <tr key={k.id}>
                    <td>
                      <b className="mono">{k.id}</b>
                      {k.id === s.keyring!.activeKeyId && (
                        <span className="chip ok" style={{ marginLeft: 6 }}>
                          Aktif
                        </span>
                      )}
                    </td>
                    <td className="mono">{k.fingerprint}</td>
                    <td className="num">{fmt.n(k.secrets)}</td>
                  </tr>
                ))}
                {s.keyring.missingKeys.map((k) => (
                  <tr key={k.id}>
                    <td>
                      <b className="mono">{k.id}</b> <span className="chip bad">Eksik</span>
                    </td>
                    <td className="muted">Yüklü değil: bu sırlar okunamıyor</td>
                    <td className="num">{fmt.n(k.secrets)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="card-b muted" style={{ fontSize: 13 }}>
            Yedekteki anahtarın parmak izi buradakiyle aynı olmalı: <span className="mono">Get-Content anahtar.txt | npm run keys:fingerprint</span>. Ayrıntı:
            docs/anahtarlar.md
          </div>
        </section>
      )}

      <section className="card">
        <div className="card-h">
          <Icon name="shield" />
          <h2>Alıcıların kişisel verileri (KVKK)</h2>
          <span className="sub">Her gece 02:00'de, gece yedeğinden önce çalışır; ayrıntı: docs/kvkk.md</span>
        </div>
        <div className="card-b muted" style={{ fontSize: 13 }}>
          {s.privacy ? (
            <>
              Kapanan siparişlerin alıcı adı, telefonu, e-postası, adres satırları ve etiket/e-belge içerikleri {s.privacy.retentionDays} gün sonra silinir
              (teslimi bilinmeyen kargo, çıkışından 30 gün sonra kapanmış sayılır). Son çalışma {fmt.dateTime(s.privacy.lastRunAt)} ({fmt.age(s.privacy.lastRunAt)} önce):{' '}
              {fmt.n(s.privacy.orders)} sipariş.
              {s.privacy.sellers.length > 0 && (
                <>
                  {' '}
                  Kendi süresi olan satıcılar: {s.privacy.sellers.map((x) => `${x.code} ${x.days} gün`).join(', ')} (Müşteriler ekranında depo yöneticisi değiştirir; Amazon
                  siparişleri her satıcıda en çok {Math.min(s.privacy.retentionDays, PRIVACY_DEFAULTS.amazonMaxDays)} gün).
                </>
              )}
            </>
          ) : (
            'Silme işi henüz hiç çalışmadı. Arka plan servisi her gece çalıştırır; elle: ./wms.sh prod privacy:erase --dry-run'
          )}
        </div>
      </section>

      {s.recent.length > 0 && (
        <details className="card">
          <summary className="card-h" style={{ cursor: 'pointer' }}>
            <Icon name="check" />
            <h2>Son 24 saatte kapanan sorunlar</h2>
            <span className="sub">{s.recent.length} kayıt</span>
          </summary>
          <div className="table-wrap">
            <table className="t">
              <tbody>
                {s.recent.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <b>{JOB_TOPIC_LABELS[r.topic] ?? r.topic}</b>
                      <div className="muted" style={{ fontSize: 12 }}>
                        {r.lastError}
                      </div>
                    </td>
                    <td>{r.occurrences} kez</td>
                    <td>
                      <span className={`chip ${r.status === 'resolved' ? 'ok' : ''}`}>{r.status === 'resolved' ? 'Kendiliğinden düzeldi' : 'Yok sayıldı'}</span>
                    </td>
                    <td className="muted">{fmt.dateTime(r.closedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </>
  );
}

function ProblemRow(props: {
  problem: OpsProblem;
  busy: string | null;
  onRetry: (failureId: string) => void;
  onDismiss: (failureId: string) => void;
  onSync: (credentialId: string) => void;
}) {
  const p = props.problem;
  return (
    <div className={`ops-item ${p.severity}`}>
      <span className={`chip ${p.severity === 'critical' ? 'bad' : 'warn'}`}>{p.severity === 'critical' ? 'Kritik' : 'Uyarı'}</span>
      <div className="ops-body">
        <b>{p.title}</b>
        {p.detail && <div className="ops-detail">{p.detail}</div>}
        <div className="muted" style={{ fontSize: 12 }}>
          {fmt.age(p.since)} önce başladı
          {p.occurrences && p.occurrences > 1 ? ` · ${p.occurrences} kez denendi` : ''}
          {p.tenantCode && (
            <>
              {' · '}
              <SellerChip code={p.tenantCode} />
            </>
          )}
          {p.orderNumber && (
            <>
              {' · '}
              <a href={`#/siparisler?q=${encodeURIComponent(p.orderNumber)}`}>sipariş {p.orderNumber}</a>
            </>
          )}
        </div>
      </div>
      <div className="ops-actions">
        {p.failureId && (
          <>
            <button className="btn sm" disabled={props.busy !== null || p.retrying} onClick={() => props.onRetry(p.failureId!)}>
              <Icon name="refresh" size={16} /> {p.retrying ? 'Gönderildi' : 'Yeniden dene'}
            </button>
            <button className="btn sm ghost" disabled={props.busy !== null} onClick={() => props.onDismiss(p.failureId!)} title="Sorunu bildiğinizi işaretler ve listeden kaldırır">
              Yok say
            </button>
          </>
        )}
        {p.kind === 'carrier' && (
          <a className="btn sm" href="#/yonetim/kargo">
            Kargo anlaşmaları
          </a>
        )}
        {!p.failureId && p.kind === 'marketplace' && p.credentialId && p.key.endsWith(':stale') && (
          <button className="btn sm" disabled={props.busy !== null} onClick={() => props.onSync(p.credentialId!)}>
            <Icon name="refresh" size={16} /> Şimdi çek
          </button>
        )}
      </div>
    </div>
  );
}
